import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Table,
  TreeSelect,
  message,
  DatePicker,
  Tooltip,
  Tag,
  Card,
  Typography,
  Drawer,
  Spin,
  Flex,
  Popover,
  Checkbox,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useAsync } from "../../../lib/useAsync";
import { errorMessage } from "../../../lib/apiError";
import { formatDateTimeCustomize } from "../../../utils/dateTime";
import {
  getListPointPromotionForCustomerActiveV2,
  getListPointPromotionHistories,
  getPointPromotionHistoryDetail,
  scanPointOrder,
} from "../../../api/pointPromotion/pointPromotion";
import { settings } from "../../../utils/config";
import { transformData } from "../../../utils/recursive";
import useStoreData from "../../../hooks/useStoreData";
import { debounce } from "lodash";
import { pointPromotionAPIWithErrorHandling } from "../../../api/pointPromotion/pointPromotionApi";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListToolbar from "../../../components/cms/ListToolbar";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import ScannedCodePanel from "./ScannedCodePanel";
import StatusTag from "../../../components/cms/StatusTag";
import {
  neutral,
  brand,
  semantic,
  spacing,
  typography,
  radius,
  border,
} from "../../../theme/tokens";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const SOURCE_OPTIONS = [
  { value: "CMS_QUANTITY", label: "Theo số lượng" },
  { value: "CMS_TRANSACTION", label: "Theo giá trị" },
  { value: "MINIAPP_SCAN", label: "Quét QR (MiniApp)" },
  { value: "CMS_MANUAL", label: "Thủ công" },
];

/**
 * Nguồn tích điểm là LOẠI, không phải trạng thái -> tất cả để tone mặc định.
 *
 * Bản cũ gán 5 màu cho 7 nguồn (cam/xanh lá/tím/lơ/xanh dương). Không ai nhớ nổi
 * 7 màu, mà nhãn chữ ("Theo SL", "Quét QR") vốn đã nói rõ rồi — màu chỉ làm bảng
 * rối thêm và cướp mất sự chú ý khỏi cột thật sự cần nhìn (điểm, trạng thái).
 */
const SOURCE_LABEL = {
  CMS_TRANSACTION: "Theo GT",
  CMS_QUANTITY: "Theo SL",
  MINIAPP_SCAN: "Quét QR",
  MINIAPP: "MiniApp",
  CMS_MANUAL: "Thủ công",
  CMS_GIFT: "Tặng điểm",
  NHANH_ORDER: "Đơn hàng",
};

const FILTER_DEFS = [
  { key: "branch", label: "Đơn vị" },
  { key: "phone", label: "Số điện thoại" },
  { key: "date", label: "Khoảng thời gian" },
  { key: "promotion", label: "Chương trình" },
  { key: "source", label: "Nguồn tích điểm" },
];

const DEFAULT_VISIBLE_FILTERS = [];

const searchReducer = (state, action) => {
  switch (action.type) {
    case "SET_BRANCH_ID":
      return { ...state, branchIdSearch: action.payload };
    case "SET_DATES":
      return { ...state, selectedDates: action.payload };
    case "SET_KEY_SEARCH":
      return { ...state, keySearch: action.payload };
    case "SET_PHONE_SEARCH":
      return { ...state, phoneSearch: action.payload };
    case "SET_PROMOTION_ID":
      return { ...state, promotionId: action.payload };
    case "SET_STATUS_ID":
      return { ...state, status: action.payload };
    case "SET_SOURCE":
      return { ...state, source: action.payload };
    case "RESET":
      return {
        branchIdSearch: 0,
        selectedDates: [],
        keySearch: null,
        phoneSearch: null,
        promotionId: null,
        status: null,
        source: null,
      };
    default:
      return state;
  }
};

// ─── Stat card ───
function StatCard({ icon, label, value, color }) {
  return (
    <div
      style={{
        background: neutral[0],
        border: border.hairline,
        borderRadius: radius.md,
        padding: `${spacing[4]}px ${spacing[5]}px`,
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: radius.md,
          background: color ? `${color}10` : neutral[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: typography.size.sm,
            color: neutral[500],
            lineHeight: typography.leading.tight,
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: typography.size["2xl"],
            fontWeight: typography.weight.bold,
            color: color || neutral[900],
            lineHeight: 1.2,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default function ScanPointOrder() {
  // Phân trang là state cục bộ của màn này (trước đây nằm trong slice
  // `pointPromotionHistories` — slice đó chỉ màn này đọc nên không cần toàn cục).
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [pointPromotionForCustomer, setPointPromotionForCustomer] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [visibleFilters, setVisibleFilters] = useState(DEFAULT_VISIBLE_FILTERS);
  const navigate = useNavigate();

  const [searchState, dispatchSearch] = useReducer(searchReducer, {
    branchIdSearch: 0,
    selectedDates: [],
    keySearch: null,
    phoneSearch: null,
    promotionId: null,
    status: null,
    source: null,
  });

  const [dashboard, setDashboard] = useState({
    total: 0,
    total_success: 0,
    total_reject: 0,
    total_review: 0,
  });

  const [allPromotion, setAllPromotion] = useState([]);

  const typeUser = settings?.getCookie("type_user");

  const dataTree = useMemo(
    () => transformData(allListStore?.data),
    [allListStore]
  );
  const options = useMemo(
    () => dataTree?.filter((item) => item.parent_id === 0),
    [dataTree]
  );

  // ─── Data fetching ───
  /**
   * Danh sách phiếu. `useAsync` truyền `AbortSignal` xuống axios nên đổi bộ lọc
   * liên tục (gõ SĐT) không còn cảnh phản hồi cũ về sau và ghi đè kết quả mới —
   * lỗi mà slice cũ không chặn được.
   */
  const historyQuery = useAsync(
    (signal) =>
      getListPointPromotionHistories(
        {
          currentPage,
          pageSize,
          mcouponBranchId: searchState.branchIdSearch,
          selectedDates: searchState.selectedDates,
          keySearch: searchState.keySearch,
          phoneSearch: searchState.phoneSearch,
          promotionId: searchState.promotionId,
          status: searchState.status,
          source: searchState.source,
        },
        signal
      ),
    [currentPage, pageSize, searchState]
  );

  const listPointPromotionHistories = historyQuery.data?.data || [];
  const isLoadPointPromotionHistories = historyQuery.loading;
  const totalRecords = historyQuery.data?.total || 0;
  const reloadHistories = historyQuery.reload;

  const fetchAllPromotion = async () => {
    try {
      const response = await pointPromotionAPIWithErrorHandling.getAll();
      setAllPromotion(response?.data?.data || []);
    } catch (error) {
      message.error("Không thể tải danh sách: " + error.message);
    }
  };

  const fetchDashboard = async () => {
    try {
      const {
        branchIdSearch,
        selectedDates,
        keySearch,
        phoneSearch,
        promotionId,
        status,
        source,
      } = searchState;
      const response = await pointPromotionAPIWithErrorHandling.getDashboard({
        mcouponBranchId: branchIdSearch,
        selectedDates,
        keySearch,
        phoneSearch,
        promotionId,
        status,
        source,
      });
      if (response?.data?.success) {
        setDashboard(response?.data?.data);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  useEffect(() => {
    fetchAllPromotion();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [searchState]);

  // ─── Handlers ───
  // Đổi bộ lọc luôn về trang 1: đang ở trang 5 mà lọc lại còn 2 trang thì bảng rỗng.
  const handleChange = (type) => (value) => {
    dispatchSearch({ type, payload: value ?? null });
    setCurrentPage(1);
  };

  const handleSearch = useCallback((value = 0) => {
    dispatchSearch({ type: "SET_BRANCH_ID", payload: value });
    setCurrentPage(1);
  }, []);

  const handleDateChange = useCallback((_, dateStrings) => {
    dispatchSearch({ type: "SET_DATES", payload: dateStrings });
    setCurrentPage(1);
  }, []);

  const handlePhoneSearch = useMemo(
    () =>
      debounce((value) => {
        dispatchSearch({ type: "SET_PHONE_SEARCH", payload: value });
        setCurrentPage(1);
      }, 800),
    []
  );
  useEffect(() => () => handlePhoneSearch.cancel(), [handlePhoneSearch]);

  /** `ListToolbar` gọi khi ẩn một ô lọc — xoá luôn giá trị để không lọc ngầm. */
  const clearFilterValue = (key) => {
    if (key === "branch") handleSearch(0);
    if (key === "phone") {
      handlePhoneSearch.cancel();
      handleChange("SET_PHONE_SEARCH")(null);
    }
    if (key === "date") handleChange("SET_DATES")([]);
    if (key === "promotion") handleChange("SET_PROMOTION_ID")(null);
    if (key === "source") handleChange("SET_SOURCE")(null);
  };

  const clearAllFilters = () => {
    handlePhoneSearch.cancel();
    dispatchSearch({ type: "RESET" });
    setCurrentPage(1);
  };

  // ─── Chi tiết phiếu tích điểm (dùng chung cho mọi nguồn Shinko) ───
  const openDetail = useCallback(
    async (record) => {
      setDetailOpen(true);
      setDetailData(null);
      setDetailLoading(true);
      try {
        const res = await getPointPromotionHistoryDetail(record.id);
        setDetailData(res?.data || null);
      } catch (e) {
        message.error(errorMessage(e, "Có lỗi khi tải chi tiết phiếu tích điểm"));
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  // ─── Scan form (branch/staff only) ───
  const fetchPromotions = useCallback(
    async (value) => {
      if (!value) {
        setPointPromotionForCustomer([]);
        return;
      }
      try {
        const response = await getListPointPromotionForCustomerActiveV2({
          orderNo: value,
          currentPagex: 1,
          pageSizex: 5000,
        });
        if (response?.total === 0) {
          message.error("Không tìm thấy khuyến mãi cho đơn hàng này.");
          form.setFieldsValue({ point_promotion_id: undefined });
          setPointPromotionForCustomer([]);
          return;
        }
        setPointPromotionForCustomer(
          (response?.data || []).map((item) => ({
            value: item.id,
            label: item.name,
          }))
        );
      } catch (error) {
        message.error(errorMessage(error));
        setPointPromotionForCustomer([]);
      }
    },
    [form]
  );

  const onFinish = useCallback(
    async (values) => {
      setIsLoading(true);
      try {
        const response = await scanPointOrder(values);
        // BE trả HTTP 200 kèm `original` khi từ chối theo nghiệp vụ.
        if (response?.original) {
          message.error(response.original.message || "Tích điểm thất bại");
          return;
        }
        message.success(response?.data || "Tích điểm thành công");
        form.resetFields();
        // Nạp lại đúng bộ lọc đang áp dụng. Bản cũ gọi
        // `getListPointPromotionHistories({ currentPage, pageSize })` trống tay nên
        // sau khi quét là danh sách mất hết bộ lọc người dùng đang đặt.
        reloadHistories();
      } catch (error) {
        message.error(errorMessage(error, "Đã xảy ra lỗi khi xử lý yêu cầu"));
      } finally {
        setIsLoading(false);
      }
    },
    // Phụ thuộc `reloadHistories` (ổn định) chứ không phải cả `historyQuery` —
    // object đó được `useAsync` tạo mới mỗi lần render nên để vào deps là
    // `onFinish` bị dựng lại liên tục, mất hết tác dụng của useCallback.
    [form, reloadHistories]
  );

  /*
   * ─── Export ───
   * Chỉ còn dựng tham số; phần tải file do `ImportExportMenu` lo (đi qua `downloadExcel`
   * nên có sẵn token + xử lý 401 + bóc message lỗi trong blob). Bản cũ tự gọi axios kèm
   * header thủ công rồi tự tạo thẻ <a>, lỗi gì cũng chỉ báo "Không thể tải file".
   */
  const exportParams = useMemo(() => {
    const { branchIdSearch, selectedDates } = searchState;

    return {
      ...(branchIdSearch !== 0 ? { mcoupon_branch_id: branchIdSearch } : {}),
      ...(selectedDates?.length > 0
        ? { start_date: selectedDates[0], end_date: selectedDates[1] }
        : {}),
    };
  }, [searchState]);

  // ─── Columns (smart) ───
  const activeSource = searchState.source;

  const columns = useMemo(() => {
    const cols = [
      {
        title: "#",
        key: "index",
        fixed: "left",
        width: 50,
        render: (_, __, i) => (
          <Text style={{ color: neutral[400], fontSize: typography.size.sm }}>
            {(currentPage - 1) * pageSize + i + 1}
          </Text>
        ),
      },
    ];

    if (typeUser === "SHOP") {
      cols.push({
        title: "Khách hàng",
        key: "customer",
        fixed: "left",
        width: 180,
        render: (_, r) => (
          <div
            onClick={() => navigate(`/customer-detail/${r.customer_id}`)}
            style={{ cursor: "pointer" }}
          >
            <Text
              style={{
                fontWeight: typography.weight.medium,
                color: brand.accent,
                display: "block",
              }}
            >
              {r.customer_t?.name || "—"}
            </Text>
            <Text
              style={{
                fontSize: typography.size.sm,
                color: neutral[500],
              }}
            >
              {r.customer_t?.phone || ""}
            </Text>
          </div>
        ),
      });
    }

    cols.push(
      {
        title: "Chi nhánh",
        key: "branch",
        width: 160,
        ellipsis: true,
        render: (_, r) => (
          <Text style={{ color: neutral[700] }}>
            {r.branch_t?.name || "—"}
          </Text>
        ),
      },
      {
        title: "Chương trình",
        key: "promotion",
        width: 200,
        ellipsis: true,
        render: (_, r) => {
          const name =
            r.point_promotion?.name || r.description || "—";
          return (
            <Tooltip title={name}>
              <Text
                ellipsis
                style={{
                  maxWidth: 180,
                  display: "inline-block",
                  color: neutral[800],
                  fontWeight: typography.weight.medium,
                }}
              >
                {name}
              </Text>
            </Tooltip>
          );
        },
      }
    );

    // Nguồn — only when "Tất cả"
    if (!activeSource) {
      cols.push({
        title: "Nguồn",
        key: "source",
        width: 120,
        render: (_, r) => <StatusTag>{SOURCE_LABEL[r.source] || r.source || "—"}</StatusTag>,
      });
    }

    // Người tạo
    cols.push({
      title: "Người tạo",
      key: "user",
      width: 140,
      ellipsis: true,
      render: (_, r) => (
        <Text style={{ color: neutral[600] }}>
          {r.user?.info?.name || "Hệ thống"}
        </Text>
      ),
    });

    cols.push({
      title: "Điểm",
      key: "point",
      align: "right",
      width: 120,
      render: (_, r) => {
        const isUp = r.fluctuation_type === "INCREASE";
        const color = isUp ? semantic.success : semantic.danger;
        const val = Number(r.point).toLocaleString("vi-VN");
        return (
          <Text style={{ fontWeight: typography.weight.semibold, color }}>
            {isUp ? `+${val}` : `-${val}`}
            {isUp ? (
              <ArrowUpOutlined style={{ marginLeft: 4, fontSize: 11 }} />
            ) : (
              <ArrowDownOutlined style={{ marginLeft: 4, fontSize: 11 }} />
            )}
          </Text>
        );
      },
    });

    cols.push({
      title: "Thời gian",
      key: "created_at",
      width: 160,
      render: (_, r) => (
        <Text style={{ fontSize: typography.size.sm, color: neutral[600] }}>
          {formatDateTimeCustomize(r.created_at)}
        </Text>
      ),
    });

    cols.push({
      title: "Chi tiết",
      key: "detail",
      align: "center",
      width: 90,
      fixed: "right",
      render: (_, r) => (
        <Tooltip title="Xem sản phẩm / số lượng / mã đã dùng">
          <Button
            type="text"
            icon={<FileTextOutlined style={{ color: brand.accent }} />}
            onClick={(e) => {
              e.stopPropagation();
              openDetail(r);
            }}
          />
        </Tooltip>
      ),
    });

    return cols;
  }, [typeUser, activeSource, currentPage, pageSize, navigate, openDetail]);

  // ─── Drawer chi tiết phiếu tích điểm (dùng chung: theo số lượng + quét QR) ───
  const renderHistoryDetailDrawer = () => {
    const items = detailData?.items || [];
    const scannedCode = detailData?.scanned_code || null;
    const srcLabel = SOURCE_LABEL[detailData?.source] || detailData?.source || "—";
    const isUp = detailData?.fluctuation_type === "INCREASE";
    const totalQty = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
    const totalPoints = items.reduce((s, it) => s + Number(it.points || 0), 0);
    const metaFields = detailData
      ? [
          {
            label: "Chương trình",
            value:
              detailData.point_promotion?.name ||
              detailData.description ||
              "—",
          },
          { label: "Người tạo", value: detailData.user?.info?.name || "Hệ thống" },
          {
            label: "Thời gian",
            value: formatDateTimeCustomize(detailData.created_at),
          },
          // Mã đã quét không nằm ở đây nữa — đã có khối riêng `ScannedCodePanel`
          // bên dưới với đầy đủ lô / sản phẩm / hạn dùng / người tạo.
        ]
      : [];

    const closeDetail = () => {
      setDetailOpen(false);
      setDetailData(null);
    };

    return (
      <Drawer
        title={
          <Flex align="center" gap={8}>
            <FileTextOutlined style={{ color: brand.accent }} />
            <span>
              Chi tiết phiếu tích điểm{detailData?.id ? ` #${detailData.id}` : ""}
            </span>
          </Flex>
        }
        open={detailOpen}
        onClose={closeDetail}
        width="min(860px, 100vw)"
        // Bảng dòng sản phẩm có thể dài; Drawer tự cuộn body nên không phải giới
        // hạn chiều cao như Modal.
        destroyOnClose
        footer={
          <Flex justify="flex-end">
            <Button onClick={closeDetail}>Đóng</Button>
          </Flex>
        }
      >
        {detailLoading ? (
          <Flex justify="center" style={{ padding: spacing[8] }}>
            <Spin size="large" />
          </Flex>
        ) : !detailData ? (
          <Empty description="Không có dữ liệu phiếu" />
        ) : (
          <>
            {/* Dải tóm tắt: khách hàng + điểm nổi bật */}
            <Flex
              justify="space-between"
              align="center"
              gap={spacing[4]}
              wrap="wrap"
              style={{
                padding: `${spacing[4]}px ${spacing[5]}px`,
                background: neutral[50],
                border: border.hairline,
                borderRadius: radius.md,
                marginBottom: spacing[4],
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Flex align="center" gap={spacing[2]} wrap="wrap" style={{ marginBottom: 2 }}>
                  <Text
                    strong
                    style={{ fontSize: typography.size.lg, color: brand.primary }}
                  >
                    {detailData.customer_t?.name || "—"}
                  </Text>
                  <StatusTag>{srcLabel}</StatusTag>
                </Flex>
                <Text style={{ color: neutral[500], fontSize: typography.size.sm }}>
                  {[detailData.customer_t?.phone, detailData.branch_t?.name]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </Text>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                  style={{
                    fontSize: typography.size["3xl"],
                    fontWeight: typography.weight.bold,
                    lineHeight: 1.1,
                    color: isUp ? semantic.success : semantic.danger,
                  }}
                >
                  {isUp ? "+" : "-"}
                  {Number(detailData.point || 0).toLocaleString("vi-VN")}
                </div>
                <Text
                  style={{
                    color: neutral[400],
                    fontSize: typography.size.xs,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Điểm tích lũy
                </Text>
              </div>
            </Flex>

            {/* Thông tin phiếu */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: `${spacing[3]}px ${spacing[5]}px`,
                paddingBottom: spacing[4],
                marginBottom: spacing[4],
                borderBottom: border.hairline,
              }}
            >
              {metaFields.map((f) => (
                <div key={f.label}>
                  <Text
                    style={{
                      display: "block",
                      color: neutral[400],
                      fontSize: typography.size.xs,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                      marginBottom: 2,
                    }}
                  >
                    {f.label}
                  </Text>
                  <div style={{ color: neutral[800], fontSize: typography.size.md }}>
                    {f.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Mã đã quét — chỉ có với nguồn Quét QR (MINIAPP_SCAN) */}
            <ScannedCodePanel
              code={scannedCode}
              record={detailData.scanned_product_code}
              items={items}
            />

            {/* Danh sách sản phẩm */}
            <Flex
              align="center"
              justify="space-between"
              style={{ marginBottom: spacing[3] }}
            >
              <Text strong style={{ color: neutral[800] }}>
                Sản phẩm tích điểm
              </Text>
              <Text style={{ color: neutral[500], fontSize: typography.size.sm }}>
                {items.length} dòng · {totalQty.toLocaleString("vi-VN")} SP
              </Text>
            </Flex>

            {items.length > 0 ? (
              <Table
                dataSource={items}
                rowKey={(r) => r.id}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                columns={[
                  {
                    title: "Sản phẩm",
                    key: "product",
                    render: (_, r) => (
                      <div>
                        <Text strong>
                          {r.product?.name || r.category_name || "—"}
                        </Text>
                        {r.product?.code ? (
                          <div>
                            <Text
                              style={{
                                color: neutral[500],
                                fontSize: typography.size.sm,
                              }}
                            >
                              {r.product.code}
                            </Text>
                          </div>
                        ) : null}
                      </div>
                    ),
                  },
                  {
                    title: "Số lượng",
                    dataIndex: "quantity",
                    align: "center",
                    width: 110,
                    render: (v) => (
                      <Text strong>{Number(v || 0).toLocaleString("vi-VN")}</Text>
                    ),
                  },
                  {
                    title: "Hệ số",
                    dataIndex: "coefficient",
                    align: "center",
                    width: 100,
                    render: (v) => Number(v || 0),
                  },
                  {
                    title: "Điểm",
                    dataIndex: "points",
                    align: "right",
                    width: 120,
                    render: (v) => (
                      <Text strong style={{ color: semantic.success }}>
                        +{Number(v || 0).toLocaleString("vi-VN")}
                      </Text>
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: neutral[50] }}>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Tổng cộng</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <Text strong>{totalQty.toLocaleString("vi-VN")}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3} align="right">
                        <Text strong style={{ color: semantic.success }}>
                          +{totalPoints.toLocaleString("vi-VN")}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            ) : (
              <Empty description="Phiếu không có dòng sản phẩm" />
            )}
          </>
        )}
      </Drawer>
    );
  };

  // ─── Render ───
  return (
    <BrandedPageLayout
      title="Lịch sử tích điểm"
      subtitle="Tổng hợp toàn bộ lịch sử tích điểm từ mọi nguồn: đơn hàng, theo số lượng, MiniApp, và thủ công."
      breadcrumbItems={[
        { title: "Lịch sử tích điểm" },
      ]}
    >
      {/* ─── Dashboard Stats ─── */}
      <Row gutter={[spacing[4], spacing[4]]} style={{ marginBottom: spacing[5] }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<HistoryOutlined style={{ fontSize: 18, color: neutral[600] }} />}
            label="Tổng"
            value={dashboard.total ?? 0}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CheckCircleOutlined style={{ fontSize: 18, color: semantic.success }} />}
            label="Thành công"
            value={dashboard.total_success ?? 0}
            color={semantic.success}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CloseCircleOutlined style={{ fontSize: 18, color: semantic.danger }} />}
            label="Từ chối"
            value={dashboard.total_reject ?? 0}
            color={semantic.danger}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<ClockCircleOutlined style={{ fontSize: 18, color: semantic.warning }} />}
            label="Đang duyệt"
            value={dashboard.total_review ?? 0}
            color={semantic.warning}
          />
        </Col>
      </Row>

      {/* ─── Scan form (branch/staff only) ─── */}
      {typeUser !== "SHOP" && (
        <Card
          size="small"
          style={{ marginBottom: spacing[4], border: border.hairline }}
          bodyStyle={{ padding: spacing[4] }}
        >
          <Form
            name="scanOrder"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            form={form}
          >
            <Row gutter={[spacing[4], 0]} align="bottom">
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Mã đơn hàng"
                  name="shop_order_id"
                  rules={[{ required: true, message: "Nhập mã đơn hàng" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Nhập mã đơn hàng"
                    onChange={(e) => fetchPromotions(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Chương trình tích điểm"
                  name="point_promotion_id"
                  rules={[{ required: true, message: "Chọn chương trình" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Chọn chương trình"
                    allowClear
                    showSearch
                    options={pointPromotionForCustomer}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  icon={<CheckCircleOutlined />}
                  block
                >
                  Hoàn tất
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      {/* Band 3 — bộ lọc + hành động (chỉ SHOP mới có quyền lọc theo đơn vị) */}
      {typeUser === "SHOP" && (
        <ListToolbar
          filterDefs={FILTER_DEFS}
          filters={{
            branch: (
              <TreeSelect
                style={{ width: "100%" }}
                disabled={isLoadStoreAll}
                placeholder="Đơn vị"
                showSearch
                allowClear
                value={searchState.branchIdSearch || undefined}
                filterTreeNode={(input, node) =>
                  node.title.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleSearch}
                treeLine
                treeData={options}
              />
            ),
            phone: (
              <Input
                placeholder="Số điện thoại"
                onChange={(e) => handlePhoneSearch(e.target.value)}
                allowClear
              />
            ),
            date: (
              <RangePicker
                style={{ width: "100%" }}
                onChange={handleDateChange}
                placeholder={["Từ ngày", "Đến ngày"]}
              />
            ),
            promotion: (
              <Select
                style={{ width: "100%" }}
                placeholder="Chương trình"
                onChange={handleChange("SET_PROMOTION_ID")}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={allPromotion.map((pt) => ({ value: pt.id, label: pt.name }))}
              />
            ),
            source: (
              <Select
                style={{ width: "100%" }}
                allowClear
                placeholder="Nguồn tích điểm"
                onChange={handleChange("SET_SOURCE")}
                options={SOURCE_OPTIONS}
              />
            ),
          }}
          onHideFilter={clearFilterValue}
          onClearFilters={clearAllFilters}
          loading={isLoadPointPromotionHistories}
          onReload={reloadHistories}
          actions={
            <ImportExportMenu
              exportUrl="/api/v1/serviceconnect/export/point-promotion-histories"
              exportParams={exportParams}
              exportFileName={`LSTD_${new Date().toISOString().slice(0, 10).replace(/-/g, "_")}.xlsx`}
              exportActions={["export.point-promotion-histories"]}
            />
          }
        />
      )}

      <CmsTableCard
        columns={columns}
        loading={isLoadPointPromotionHistories}
        dataSource={listPointPromotionHistories || []}
        rowKey="id"
        pagination={{
          showTotal: (total, range) => `${range[0]}–${range[1]} / ${total}`,
          onShowSizeChange: (_, size) => setPageSize(size),
          total: totalRecords,
          current: currentPage,
          pageSize,
          onChange: (page) => setCurrentPage(page),
        }}
        locale={{
          emptyText: (
            <Empty description="Chưa có lịch sử tích điểm" style={{ padding: spacing[8] }} />
          ),
        }}
        bordered
        scroll={{ x: "max-content" }}
      />

      {renderHistoryDetailDrawer()}
    </BrandedPageLayout>
  );
}
